import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { useNavigate, useParams } from "react-router-dom";
import { api, authFetch } from "../utils/api";
import { getSocket } from "../utils/socket";
import { useCall } from "../context/CallContext";
import { compressImage } from "../utils/imageCompress";
import { Conversation, Contact, ChatMessage } from "../components/messages/types";
import { callLogLabel, dateSeparatorLabel, extractLegacyWatchRoomId, formatBubbleTime, messagesGroup } from "../components/messages/helpers";
import { Avatar, vibeRingKind } from "../components/Avatar";
import { ChatEmptyIcon, LockIcon, FlameIcon, MuteIcon, EyeOffIcon, TrashIcon, ScreenshotOffIcon, PinIcon, ToggleSwitch, CallIcon, VideoCallIcon, WatchTogetherIcon } from "../components/messages/icons";
import { ConversationRow } from "../components/messages/ConversationRow";
import { ChatHeader } from "../components/messages/ChatHeader";
import { MessageBubble } from "../components/messages/MessageBubble";
import DateConfirmedMessage from "../components/date/DateConfirmedMessage";
import { Composer } from "../components/messages/Composer";
import { ConfirmSheet } from "../components/messages/ConfirmSheet";
import { HiddenChatsHandle } from "../components/messages/HiddenChatsHandle";
import { useIsMobile } from "../hooks/useIsMobile";

const TAB_TO_TYPE: Record<string, Conversation["type"] | null> = {
  all: null, friends: "direct", groups: "community", rituals: "ritual",
};
const TAB_I18N_KEY: Record<string, string> = {
  all: "msg_all", friends: "msg_friends", groups: "msg_groups", rituals: "msg_rituals",
};
const TABS = Object.keys(TAB_TO_TYPE);
const DISAPPEAR_OPTIONS = [{ label: "10 s", v: 10 }, { label: "1 min", v: 60 }, { label: "1 h", v: 3600 }, { label: "24 h", v: 86400 }];

export default function Messages() {
  const { lang, user, token } = useApp();
  const { startCall } = useCall();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [activeTab, setActiveTab] = useState("friends");
  const [search, setSearch] = useState("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selected, setSelected] = useState<Conversation | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState("");
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showWatchModal, setShowWatchModal] = useState(false);
  const [watchUrl, setWatchUrl] = useState("");
  const [creatingWatchRoom, setCreatingWatchRoom] = useState(false);
  // Bandeau de prudence — voir le plan "Trois emprunts..." : signal calculé côté serveur
  // (GET /messages/:id), fermeture pour la session en cours seulement (pas de nouvelle colonne,
  // état local qui repart à zéro au rechargement — un fast-follow possible si le fondateur veut
  // que ce soit permanent).
  const [safetySignal, setSafetySignal] = useState<{ mutual_friends_count: number; account_age_days: number } | null>(null);
  const [safetyBannerDismissed, setSafetyBannerDismissed] = useState(false);
  const [showReportField, setShowReportField] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [submittingReport, setSubmittingReport] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  // Étape d'écoute avant envoi d'un vocal — jamais d'envoi automatique à l'arrêt de
  // l'enregistrement (voir startRecording/stopRecording plus bas).
  const [voicePreview, setVoicePreview] = useState<{ url: string; blob: Blob } | null>(null);
  const [sendingVoice, setSendingVoice] = useState(false);

  // Pagination "messages précédents" (GET /messages/:id?before=...&limit=...).
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  // Id du premier message non lu au chargement initial de la discussion — sert à placer une
  // seule fois la ligne "Nouveaux messages" ; jamais recalculé pour les messages reçus en direct
  // ensuite (ce ne sont plus des messages qu'on "rattrape", juste de nouveaux messages normaux).
  const [unreadDividerMsgId, setUnreadDividerMsgId] = useState<string | null>(null);
  // Bulles dont l'heure+coches a été révélée ponctuellement par un tap (regroupement des
  // messages consécutifs — voir groupedMeta plus bas).
  const [revealedIds, setRevealedIds] = useState<Set<string>>(new Set());
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasNewMessagesBelow, setHasNewMessagesBelow] = useState(false);
  // Notice transitoire (erreurs, rate-limit...) — remplace les alert()/confirm() natifs partout
  // dans cet écran.
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDeleteMsg, setConfirmDeleteMsg] = useState<ChatMessage | null>(null);
  const [confirmDeleteConv, setConfirmDeleteConv] = useState(false);

  // Discussions masquées, protégées par un code — façon "Locked Chats" de WhatsApp
  const [viewingHidden, setViewingHidden] = useState(false);
  const [hiddenConversations, setHiddenConversations] = useState<Conversation[]>([]);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinMode, setPinMode] = useState<"setup" | "confirm" | "verify">("verify");
  const [pinValue, setPinValue] = useState("");
  const [pinFirstEntry, setPinFirstEntry] = useState("");
  const [pinError, setPinError] = useState("");
  // Accusé de lecture (coches) : instant jusqu'auquel l'autre participant a lu la discussion.
  const [otherReadAt, setOtherReadAt] = useState<string | null>(null);
  const [messageMenuId, setMessageMenuId] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const listScrollRef = useRef<HTMLDivElement>(null);

  const chatScrollRef = useRef<HTMLDivElement>(null);
  const typingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Scroll orchestration -------------------------------------------------------------
  // scrollActionRef pilote ce que fait le useEffect ci-dessous après le prochain rendu de
  // chatMessages : "bottom" (chargement initial, message qu'on vient d'envoyer soi-même),
  // "preserve" (pagination : on préfixe d'anciens messages sans faire sauter la vue) ou
  // "auto" (message reçu en direct : on ne descend que si on était déjà en bas).
  const isAtBottomRef = useRef(true);
  const scrollActionRef = useRef<"bottom" | "preserve" | "auto" | null>(null);
  const preserveScrollRef = useRef<{ scrollHeight: number; scrollTop: number } | null>(null);
  const ackTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const noticeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Garde-fou synchrone pour loadOlderMessages : le state loadingOlder ne se met à jour qu'au
  // rendu suivant, donc deux appels très rapprochés (double-clic, ou un clic qui rejoue) peuvent
  // tous les deux passer le test "if (loadingOlder) return" avant que le premier ait eu le temps
  // de le faire passer à true. Une ref, elle, est lue/écrite immédiatement.
  const loadingOlderRef = useRef(false);

  const showNotice = (msg: string) => {
    setNotice(msg);
    if (noticeTimeoutRef.current) clearTimeout(noticeTimeoutRef.current);
    noticeTimeoutRef.current = setTimeout(() => setNotice(null), 3500);
  };

  const loadConversations = useCallback(() => {
    api.get("/messages").then(data => {
      if (data && Array.isArray(data.conversations)) setConversations(data.conversations);
    }).catch(() => {});
  }, []);

  const loadHiddenConversations = useCallback(() => {
    api.get("/messages?hidden=true").then(data => {
      if (data && Array.isArray(data.conversations)) setHiddenConversations(data.conversations);
    }).catch(() => {});
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    api.get("/messages/meta/contacts").then(data => {
      if (data && Array.isArray(data.contacts)) setContacts(data.contacts);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!token) return;
    const socket = getSocket(token);

    const onNewMessage = ({ conversationId, message }: any) => {
      setSelected(sel => {
        if (sel && sel.id === conversationId) {
          scrollActionRef.current = "auto";
          // Filtre défensif par id : une reconnexion socket peut, dans de rares cas, redélivrer
          // un événement déjà reçu — on ne pousse jamais deux fois le même message.
          setChatMessages(prev => prev.some(m => m.id === message.id) ? prev : [...prev, message]);
        }
        return sel;
      });
      loadConversations();
    };
    const onTyping = ({ conversationId, userName }: any) => {
      setSelected(sel => {
        if (sel && sel.id === conversationId) {
          setTypingUser(userName);
          if (typingTimeout.current) clearTimeout(typingTimeout.current);
          typingTimeout.current = setTimeout(() => setTypingUser(null), 2500);
        }
        return sel;
      });
    };
    const onPresence = ({ userId, online }: any) => {
      setConversations(prev => prev.map(c => c.other_user_id === userId ? { ...c, online } : c));
      setContacts(prev => prev.map(c => c.id === userId ? { ...c, online } : c));
      setSelected(sel => sel && sel.other_user_id === userId ? { ...sel, online } : sel);
    };
    // Mode éphémère : les messages lus sont retirés après quelques secondes des deux côtés.
    const onMessagesDeleted = ({ conversationId, messageIds }: any) => {
      setSelected(sel => {
        if (sel && sel.id === conversationId) {
          setChatMessages(prev => prev.filter(m => !messageIds.includes(m.id)));
        }
        return sel;
      });
    };
    const onSettingsChanged = ({ conversationId, disappearing, disappearing_duration_seconds, block_screenshots }: any) => {
      setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, disappearing, disappearing_duration_seconds, block_screenshots } : c));
      setSelected(sel => sel && sel.id === conversationId ? { ...sel, disappearing, disappearing_duration_seconds, block_screenshots } : sel);
    };
    // Le web ne peut ni bloquer ni détecter une capture lui-même, mais mérite quand même de
    // savoir qu'une capture a eu lieu côté mobile (signalé uniquement par le client iOS de
    // l'auteur, voir POST /messages/:id/screenshot) — même logique que mobile, jamais pour
    // soi-même.
    const onScreenshotTaken = ({ conversationId, userId }: any) => {
      if (userId === user?.id) return;
      setSelected(sel => {
        if (sel && sel.id === conversationId) showNotice(t(lang,"msg_screenshot_taken_notice")||"📸 Capture d'écran de la discussion");
        return sel;
      });
    };
    // L'autre participant vient de lire la discussion : mes messages envoyés avant cet
    // instant passent en "lu" (double coche), en direct, sans recharger la conversation.
    const onConversationRead = ({ conversationId, userId, readAt }: any) => {
      if (userId === user?.id) return;
      setSelected(sel => {
        if (sel && sel.id === conversationId) {
          setOtherReadAt(prev => (!prev || readAt > prev) ? readAt : prev);
        }
        return sel;
      });
    };
    const onMessageEdited = ({ conversationId, message }: any) => {
      setSelected(sel => {
        if (sel && sel.id === conversationId) {
          setChatMessages(prev => prev.map(m => m.id === message.id ? message : m));
        }
        return sel;
      });
    };

    socket.on("new_message", onNewMessage);
    socket.on("user_typing", onTyping);
    socket.on("presence_update", onPresence);
    socket.on("messages_deleted", onMessagesDeleted);
    socket.on("conversation_settings_changed", onSettingsChanged);
    socket.on("screenshot_taken", onScreenshotTaken);
    socket.on("conversation_read", onConversationRead);
    socket.on("message_edited", onMessageEdited);

    return () => {
      socket.off("new_message", onNewMessage);
      socket.off("user_typing", onTyping);
      socket.off("presence_update", onPresence);
      socket.off("messages_deleted", onMessagesDeleted);
      socket.off("conversation_settings_changed", onSettingsChanged);
      socket.off("screenshot_taken", onScreenshotTaken);
      socket.off("conversation_read", onConversationRead);
      socket.off("message_edited", onMessageEdited);
    };
  }, [token, loadConversations, user?.id, lang]);

  // Après chaque mise à jour de chatMessages, applique l'action de scroll décidée par
  // l'appelant (voir scrollActionRef ci-dessus) — jamais de scroll-to-bottom aveugle : c'est
  // ce qui permettait auparavant à un nouveau message reçu de "voler" la position de lecture
  // de quelqu'un qui remontait l'historique.
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const action = scrollActionRef.current;
    scrollActionRef.current = null;
    if (action === "bottom") {
      // scrollTop direct sur le conteneur plutôt que scrollIntoView : sur iOS Safari,
      // scrollIntoView remonte parfois la chaîne des ancêtres et perturbe l'affichage plein
      // écran de la discussion — scrollTop reste, lui, strictement local au conteneur.
      el.scrollTop = el.scrollHeight;
      isAtBottomRef.current = true;
      setIsAtBottom(true);
      setHasNewMessagesBelow(false);
    } else if (action === "preserve" && preserveScrollRef.current) {
      const { scrollHeight: oldHeight, scrollTop: oldTop } = preserveScrollRef.current;
      el.scrollTop = oldTop + (el.scrollHeight - oldHeight);
      preserveScrollRef.current = null;
    } else if (action === "auto") {
      if (isAtBottomRef.current) el.scrollTop = el.scrollHeight;
      else setHasNewMessagesBelow(true);
    }
  }, [chatMessages]);

  const handleChatScroll = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
    if (atBottom) setHasNewMessagesBelow(false);
  };

  const scrollToBottom = () => {
    const el = chatScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    setHasNewMessagesBelow(false);
  };

  const getFiltered = () => {
    const wantType = TAB_TO_TYPE[activeTab];
    let list = wantType ? conversations.filter(c => c.type === wantType) : conversations;
    if (search) list = list.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()));
    return list;
  };

  const totalUnread = conversations.reduce((acc, c) => acc + (c.unread || 0), 0);

  // Ouvre une discussion via une vraie URL (/messages/:id) plutôt qu'un simple état local :
  // le bouton retour ET le geste natif de balayage arrière du téléphone fonctionnent alors
  // comme sur n'importe quelle appli, puisqu'il y a une vraie entrée d'historique à dépiler.
  // On affiche l'objet déjà connu tout de suite, sans attendre la navigation/le rechargement.
  const goToConversation = (conv: Conversation) => {
    setSelected(conv);
    setTypingUser(null);
    setShowAttachMenu(false);
    setShowSettingsPanel(false);
    navigate(`/messages/${conv.id}`);
  };

  // Si on arrive directement sur /messages/:id sans passer par goToConversation (rechargement
  // de page, navigation précédente/suivante du navigateur), on retrouve la discussion depuis
  // les listes déjà chargées.
  useEffect(() => {
    if (!conversationId) { setSelected(null); return; }
    setSelected(sel => {
      if (sel && sel.id === conversationId) return sel;
      return conversations.find(c => c.id === conversationId) || hiddenConversations.find(c => c.id === conversationId) || sel;
    });
  }, [conversationId, conversations, hiddenConversations]);

  // Charge les messages et rejoint la discussion — une seule fois par changement d'URL, pas à
  // chaque rafraîchissement de la liste des conversations.
  useEffect(() => {
    if (!conversationId || !token) return;
    setOtherReadAt(null);
    setUnreadDividerMsgId(null);
    setHasMoreOlder(false);
    setRevealedIds(new Set());
    setSafetySignal(null);
    setSafetyBannerDismissed(false);
    setShowReportField(false);
    setReportReason("");
    api.get(`/messages/${conversationId}`).then(data => {
      if (data && Array.isArray(data.messages)) {
        scrollActionRef.current = "bottom";
        setChatMessages(data.messages);
        setHasMoreOlder(!!data.has_more);
        setSafetySignal(data.safety_signal || null);
        const lastRead: string | null = data.my_last_read_at || null;
        if (lastRead) {
          const firstUnread = data.messages.find((m: any) => m.created_at > lastRead);
          setUnreadDividerMsgId(firstUnread ? firstUnread.id : null);
        }
      }
      const reads: { last_read_at: string | null }[] = data?.other_reads || [];
      const latest = reads.map(r => r.last_read_at).filter(Boolean).sort().pop();
      if (latest) setOtherReadAt(latest);
    }).catch(() => {});
    const socket = getSocket(token);
    socket.emit("join_conversation", conversationId);
    socket.emit("mark_read", conversationId);
    socket.emit("set_active_conversation", conversationId);
    setConversations(prev => prev.map(c => c.id === conversationId ? { ...c, unread: 0 } : c));
    return () => { socket.emit("set_active_conversation", null); };
  }, [conversationId, token]);

  // Socket.IO reconnecte automatiquement tout seul après une coupure, et rejoue déjà
  // "join_conversations" (toutes les discussions) à la reconnexion (voir utils/socket.ts) —
  // mais pas le join_conversation/mark_read/set_active_conversation de LA discussion
  // actuellement ouverte. Sans ça, après une coupure réseau, on continue d'afficher une
  // discussion "active" côté client alors que le serveur ne le sait plus (pas de mark_read,
  // pas de statut "en train d'écrire" reçu, etc.).
  useEffect(() => {
    if (!conversationId || !token) return;
    const socket = getSocket(token);
    const resync = () => {
      socket.emit("join_conversation", conversationId);
      socket.emit("mark_read", conversationId);
      socket.emit("set_active_conversation", conversationId);
    };
    socket.on("connect", resync);
    return () => { socket.off("connect", resync); };
  }, [conversationId, token]);

  // Démarre (ou rouvre) une discussion directe depuis les "têtes" d'amis en haut de la liste,
  // comme sur Messenger — plus besoin de passer par une conversation déjà existante.
  const startConversation = (contact: Contact) => {
    api.post(`/messages/direct/${contact.id}`, {}).then(data => {
      if (!data?.conversationId) { if (data?.error) alert(data.error); return; }
      loadConversations();
      setChatMessages([]);
      goToConversation({
        id: data.conversationId, type: "direct", other_user_id: contact.id,
        name: contact.name, avatar_url: contact.avatar_url, online: contact.online, unread: 0,
      });
    }).catch(() => {});
  };

  // --- Envoi optimiste (client_id + accusé côté socket) ---------------------------------
  // Chaque envoi (texte ou média) pousse d'abord une bulle locale en statut "sending", avec un
  // client_id généré ici. Le serveur fait un insert idempotent sur (conversation_id, client_id) :
  // renvoyer strictement le même client_id (ex: lors d'un tap "réessayer") ne peut donc jamais
  // créer de doublon, même si le premier envoi avait en fait réussi côté serveur mais que son
  // accusé s'était perdu en route.
  const clearAckTimeout = (clientId: string) => {
    const existing = ackTimeoutsRef.current.get(clientId);
    if (existing) { clearTimeout(existing); ackTimeoutsRef.current.delete(clientId); }
  };

  const emitSend = (payload: { conversationId: string; content?: string; type?: string; media_url?: string; client_id: string }) => {
    if (!token) return;
    clearAckTimeout(payload.client_id);
    // Pas d'accusé sous ~8s : on considère l'envoi en échec côté UI (le tap "réessayer" ré-émet
    // ensuite avec le même client_id, ce qui reste sûr même si l'accusé arrivait finalement en
    // retard entre-temps).
    const timeoutId = setTimeout(() => {
      ackTimeoutsRef.current.delete(payload.client_id);
      setChatMessages(prev => prev.map(m => m.client_id === payload.client_id && m.status === "sending" ? { ...m, status: "failed" } : m));
    }, 8000);
    ackTimeoutsRef.current.set(payload.client_id, timeoutId);
    getSocket(token).emit("send_message", payload, (res: any) => {
      clearAckTimeout(payload.client_id);
      if (res?.message) {
        setChatMessages(prev => prev.map(m => m.client_id === payload.client_id ? { ...res.message, status: "sent", client_id: payload.client_id } : m));
        loadConversations();
      } else {
        setChatMessages(prev => prev.map(m => m.client_id === payload.client_id ? { ...m, status: "failed" } : m));
        if (res?.error) showNotice(res.error);
      }
    });
  };

  const sendMessage = () => {
    if (!inputText.trim() || !selected || !token) return;
    if (editingMessageId) {
      api.put(`/messages/${selected.id}/messages/${editingMessageId}`, { content: inputText.trim() }).then(data => {
        if (data?.error) { showNotice(data.error); return; }
        if (data?.message) setChatMessages(prev => prev.map(m => m.id === data.message.id ? data.message : m));
        setEditingMessageId(null);
        setInputText("");
      }).catch(() => {});
      return;
    }
    const clientId = crypto.randomUUID();
    const content = inputText.trim();
    const optimistic: ChatMessage = {
      id: clientId, client_id: clientId, conversation_id: selected.id, user_id: user?.id, user_name: user?.name,
      content, type: "text", created_at: new Date().toISOString(), status: "sending",
    };
    scrollActionRef.current = "bottom";
    setChatMessages(prev => [...prev, optimistic]);
    setInputText("");
    emitSend({ conversationId: selected.id, content, type: "text", client_id: clientId });
  };

  const startEditMessage = (msg: ChatMessage) => {
    setMessageMenuId(null);
    setEditingMessageId(msg.id);
    setInputText(msg.content || "");
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setInputText("");
  };

  const deleteMessage = (msg: ChatMessage) => {
    setMessageMenuId(null);
    setConfirmDeleteMsg(msg);
  };

  const performDeleteMessage = () => {
    if (!selected || !confirmDeleteMsg) return;
    const msg = confirmDeleteMsg;
    setConfirmDeleteMsg(null);
    api.delete(`/messages/${selected.id}/messages/${msg.id}`).then(data => {
      if (data?.error) { showNotice(data.error); return; }
      setChatMessages(prev => prev.filter(m => m.id !== msg.id));
      if (editingMessageId === msg.id) cancelEditMessage();
    }).catch(() => {});
  };

  const sendMediaMessage = (type: string, mediaUrl: string, content: string = "") => {
    if (!selected || !token) return;
    const clientId = crypto.randomUUID();
    const optimistic: ChatMessage = {
      id: clientId, client_id: clientId, conversation_id: selected.id, user_id: user?.id, user_name: user?.name,
      content, type, media_url: mediaUrl || undefined, created_at: new Date().toISOString(), status: "sending",
    };
    scrollActionRef.current = "bottom";
    setChatMessages(prev => [...prev, optimistic]);
    emitSend({ conversationId: selected.id, content, type, media_url: mediaUrl || undefined, client_id: clientId });
  };

  // "Regarder ensemble" — crée la salle puis partage son lien comme un message dans CETTE
  // discussion : réutilise directement "avec qui" (une conversation, c'est déjà le bon groupe de
  // personnes), pas besoin d'un sélecteur de contacts séparé. `type: "watch_room"` plutôt qu'un
  // texte brut avec un émoji 🎬 : "type" est déjà un champ libre côté serveur (voir "call",
  // "date_confirmed", aucune validation dans socket/index.ts#send_message), et un vrai rendu en
  // pastille SVG (WatchTogetherIcon, même icône que le bouton de l'en-tête) reste cohérent avec
  // le reste du produit, contrairement à un émoji brut dans une bulle de texte.
  const startWatchTogether = async () => {
    if (!selected || !watchUrl.trim() || creatingWatchRoom) return;
    setCreatingWatchRoom(true);
    try {
      const data = await api.post("/watch-rooms", { url: watchUrl.trim() });
      if (data?.error || !data?.room) { showNotice(data?.error || "Lien YouTube invalide"); return; }
      const clientId = crypto.randomUUID();
      const content = JSON.stringify({ room_id: data.room.id, video_title: data.room.video_title });
      const optimistic: ChatMessage = {
        id: clientId, client_id: clientId, conversation_id: selected.id, user_id: user?.id, user_name: user?.name,
        content, type: "watch_room", created_at: new Date().toISOString(), status: "sending",
      };
      scrollActionRef.current = "bottom";
      setChatMessages(prev => [...prev, optimistic]);
      emitSend({ conversationId: selected.id, content, type: "watch_room", client_id: clientId });
      setShowWatchModal(false);
      setWatchUrl("");
      navigate(`/watch/${data.room.id}`);
    } catch {
      showNotice("Erreur serveur");
    } finally {
      setCreatingWatchRoom(false);
    }
  };

  const blockFromSafetyBanner = () => {
    if (!selected?.other_user_id) return;
    if (!window.confirm(`Bloquer ${selected.name} ? Vous ne pourrez plus vous contacter, ni vous voir en amis.`)) return;
    api.post(`/users/${selected.other_user_id}/block`, {}).then(() => {
      navigate("/messages");
    }).catch(() => showNotice("Erreur serveur"));
  };

  const submitSafetyReport = () => {
    if (!selected?.other_user_id || !reportReason.trim() || submittingReport) return;
    setSubmittingReport(true);
    api.post(`/users/${selected.other_user_id}/report`, { reason: reportReason.trim(), conversation_id: selected.id }).then(data => {
      if (data?.error) { showNotice(data.error); return; }
      showNotice(t(lang, "safety_report_sent") || "Signalement envoyé");
      setShowReportField(false);
      setReportReason("");
      setSafetyBannerDismissed(true);
    }).catch(() => showNotice("Erreur serveur")).finally(() => setSubmittingReport(false));
  };

  const retryMessage = (msg: ChatMessage) => {
    if (!selected || !token || !msg.client_id || msg.status !== "failed") return;
    setChatMessages(prev => prev.map(m => m.client_id === msg.client_id ? { ...m, status: "sending" } : m));
    emitSend({ conversationId: selected.id, content: msg.content, type: msg.type, media_url: msg.media_url, client_id: msg.client_id });
  };

  const uploadChatMedia = async (file: File): Promise<string> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await authFetch("/uploads/message", { method: "POST", body: fd });
    const data = await res.json();
    if (!data?.url) throw new Error("upload failed");
    return data.url;
  };

  const handleGallerySelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingMedia(true);
    try {
      const compressed = await compressImage(file);
      const url = await uploadChatMedia(compressed);
      sendMediaMessage("image", url);
    } catch {
      showNotice(t(lang,"msg_error_upload_photo")||"Erreur lors de l'envoi de la photo");
    } finally {
      setUploadingMedia(false);
    }
  };

  const sendFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingMedia(true);
    try {
      const url = await uploadChatMedia(file);
      sendMediaMessage("file", url, file.name);
    } catch {
      showNotice(t(lang,"msg_error_upload_file")||"Erreur lors de l'envoi du fichier");
    } finally {
      setUploadingMedia(false);
    }
  };

  const shareLocation = () => {
    setShowAttachMenu(false);
    if (!navigator.geolocation) { showNotice(t(lang,"msg_error_geo_unavailable")||"Géolocalisation non disponible"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => sendMediaMessage("location", "", JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude })),
      () => showNotice(t(lang,"msg_error_geo_failed")||"Impossible d'obtenir votre position")
    );
  };

  // Nettoie l'URL objet du preview vocal si on change de discussion / démonte l'écran sans
  // avoir envoyé ni recommencé.
  useEffect(() => () => { if (voicePreview) URL.revokeObjectURL(voicePreview.url); }, [voicePreview]);

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(tr => tr.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        // Jamais d'envoi automatique à l'arrêt — l'utilisateur doit pouvoir réécouter et
        // confirmer explicitement (ou recommencer) avant que le vocal parte vraiment.
        if (blob.size >= 500) setVoicePreview({ url: URL.createObjectURL(blob), blob });
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch {
      showNotice(t(lang,"msg_error_mic")||"Micro indisponible ou autorisation refusée");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const discardVoicePreview = () => {
    if (voicePreview) URL.revokeObjectURL(voicePreview.url);
    setVoicePreview(null);
  };

  const sendVoicePreview = async () => {
    if (!voicePreview || sendingVoice) return;
    setSendingVoice(true);
    try {
      const url = await uploadChatMedia(new File([voicePreview.blob], "vocal.webm", { type: "audio/webm" }));
      sendMediaMessage("audio", url);
      discardVoicePreview();
    } catch {
      showNotice(t(lang,"msg_error_upload_voice")||"Erreur lors de l'envoi du vocal");
    } finally {
      setSendingVoice(false);
    }
  };

  // Applique un ou plusieurs réglages à la discussion sélectionnée (éphémère/durée/épingler/
  // sourdine/masquer) et met à jour l'état local sans recharger toute la liste.
  const updateConvSettings = (patch: Partial<Pick<Conversation, "disappearing" | "disappearing_duration_seconds" | "block_screenshots" | "pinned" | "muted" | "hidden">>) => {
    if (!selected) return;
    const id = selected.id;
    api.put(`/messages/${id}/settings`, patch).then(data => {
      if (data?.error) { showNotice(data.error); return; }
      setSelected(sel => sel && sel.id === id ? { ...sel, ...patch } : sel);
      setConversations(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
      setHiddenConversations(prev => prev.map(c => c.id === id ? { ...c, ...patch } : c));
      // Masquer une discussion la sort immédiatement de la vue courante ; ne plus la masquer
      // depuis la vue "masquées" fait l'inverse.
      if (patch.hidden === true) { navigate("/messages"); loadConversations(); }
      if (patch.hidden === false && viewingHidden) { loadHiddenConversations(); }
    }).catch(() => {});
  };

  const deleteConversation = () => {
    if (!selected) return;
    setConfirmDeleteConv(true);
  };

  const performDeleteConversation = () => {
    if (!selected) return;
    setConfirmDeleteConv(false);
    const id = selected.id;
    api.delete(`/messages/${id}`).then(data => {
      if (data?.error) { showNotice(data.error); return; }
      setShowSettingsPanel(false);
      navigate("/messages");
      loadConversations();
      if (viewingHidden) loadHiddenConversations();
    }).catch(() => {});
  };

  const handleTyping = () => {
    if (!selected || !token) return;
    getSocket(token).emit("typing", selected.id);
  };

  const handleAvatarClick = (conv: Conversation) => {
    if (conv.type === "direct" && conv.other_user_id) navigate("/profile/" + conv.other_user_id);
    else if (conv.type === "community" && conv.community_id) navigate("/community/" + conv.community_id);
    else if (conv.type === "ritual" && conv.ritual_id) navigate("/ritual/" + conv.ritual_id);
    else if (conv.type === "birthday_event" && conv.birthday_event_id) navigate("/birthday-events/" + conv.birthday_event_id);
  };

  // Charge la page de messages précédant le plus ancien message actuellement affiché, et
  // préserve la position de lecture visible à l'écran (pas de saut visuel) en compensant le
  // scrollTop de la différence de hauteur ajoutée en haut du conteneur.
  const loadOlderMessages = () => {
    if (!conversationId || !token || loadingOlderRef.current || chatMessages.length === 0) return;
    loadingOlderRef.current = true;
    const oldest = chatMessages[0];
    setLoadingOlder(true);
    api.get(`/messages/${conversationId}?before=${encodeURIComponent(oldest.created_at)}&limit=50`).then(data => {
      if (data && Array.isArray(data.messages) && data.messages.length > 0) {
        const el = chatScrollRef.current;
        preserveScrollRef.current = el ? { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop } : null;
        scrollActionRef.current = "preserve";
        // Filtre défensif par id : si cet appel chevauche malgré tout un autre chargement (page
        // déjà présente), on ne duplique jamais une entrée déjà affichée.
        setChatMessages(prev => {
          const existingIds = new Set(prev.map(m => m.id));
          const fresh = data.messages.filter((m: ChatMessage) => !existingIds.has(m.id));
          return [...fresh, ...prev];
        });
      }
      setHasMoreOlder(!!data?.has_more);
    }).catch(() => {}).finally(() => { loadingOlderRef.current = false; setLoadingOlder(false); });
  };

  // Code des discussions masquées : demande de création s'il n'existe pas encore, sinon vérification.
  const openHiddenChats = () => {
    setPinValue(""); setPinFirstEntry(""); setPinError("");
    api.get("/users/me/chat-lock").then(data => {
      setPinMode(data?.configured ? "verify" : "setup");
      setShowPinModal(true);
    }).catch(() => {});
  };

  const submitPin = () => {
    if (!/^\d{4,6}$/.test(pinValue)) { setPinError(t(lang,"msg_pin_error_format")||"Le code doit contenir 4 à 6 chiffres"); return; }
    if (pinMode === "setup") {
      setPinFirstEntry(pinValue); setPinValue(""); setPinError(""); setPinMode("confirm");
      return;
    }
    if (pinMode === "confirm") {
      if (pinValue !== pinFirstEntry) {
        setPinError(t(lang,"msg_pin_error_mismatch")||"Les codes ne correspondent pas"); setPinValue(""); setPinFirstEntry(""); setPinMode("setup");
        return;
      }
      api.post("/users/me/chat-lock", { pin: pinValue }).then(data => {
        if (data?.error) { setPinError(data.error); return; }
        setShowPinModal(false); setViewingHidden(true); loadHiddenConversations();
      }).catch(() => setPinError(t(lang,"msg_pin_error_server")||"Erreur serveur"));
      return;
    }
    // verify
    api.post("/users/me/chat-lock/verify", { pin: pinValue }).then(data => {
      if (data?.valid) { setShowPinModal(false); setViewingHidden(true); loadHiddenConversations(); }
      else { setPinError(t(lang,"msg_pin_error_wrong")||"Code incorrect"); setPinValue(""); }
    }).catch(() => setPinError(t(lang,"msg_pin_error_server")||"Erreur serveur"));
  };

  const filtered = getFiltered();
  const showList = !isMobile || !selected;
  const showChat = !!selected;

  // Regroupement des messages consécutifs (même expéditeur, même jour, < 60s d'écart) : calculé
  // une fois par changement de chatMessages plutôt qu'à chaque bulle, pour que MessageBubble
  // reste un composant purement présentationnel.
  const groupedMeta = React.useMemo(() => {
    return chatMessages.map((msg, i) => {
      if (msg.type === "call" || msg.type === "watch_room") return { isGroupStart: true, isGroupEnd: true };
      const prev = chatMessages[i - 1];
      const next = chatMessages[i + 1];
      return {
        isGroupStart: !messagesGroup(prev as any, msg as any),
        isGroupEnd: !messagesGroup(msg as any, next as any),
      };
    });
  }, [chatMessages]);

  const toggleReveal = (id: string) => {
    setRevealedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", height: "100%", overflow: "hidden" }}>

      {/* PANNEAU GAUCHE — liste. height:"100%" est indispensable en mobile : le conteneur racine
          passe en flexDirection:"column", et sans hauteur explicite ce panneau se dimensionne
          sur son contenu (donc plus grand que l'écran) au lieu de l'espace réellement
          disponible — la liste se retrouvait tronquée sans aucun moyen de faire défiler le reste. */}
      {showList && (
      <div style={{ width: isMobile ? "100%" : 360, height: "100%", flexShrink: 0, borderRight: isMobile ? "none" : "1px solid #F0F0F0", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "20px 16px 12px", borderBottom: "1px solid #F0F0F0" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20, fontWeight: 700 }}>{t(lang,"messages")||"Messages"}</span>
              {totalUnread > 0 && <span style={{ backgroundColor: "#0a0a0a", color: "white", borderRadius: 20, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>{totalUnread}</span>}
            </div>
          </div>

          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: "#F5F5F5", borderRadius: 12, padding: "9px 12px" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t(lang,"msg_search")||"Rechercher..."} style={{ border: "none", outline: "none", fontSize: 14, flex: 1, backgroundColor: "transparent" }} />
            {search && <button onClick={() => setSearch("")} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 16 }}>✕</button>}
          </div>
        </div>

        {/* Têtes des amis connectés — cliquer en démarre (ou rouvre) directement la discussion,
            comme sur Messenger. */}
        {!viewingHidden && contacts.length > 0 && (
          <div style={{ display: "flex", gap: 16, overflowX: "auto", padding: "14px 16px", borderBottom: "1px solid #F0F0F0" }}>
            {contacts.map(c => (
              <div key={c.id} onClick={() => startConversation(c)} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5, cursor: "pointer", flexShrink: 0, width: 54 }}>
                <Avatar id={c.id} name={c.name} uri={c.avatar_url} size={46} online={c.online} showOnline ringKind={vibeRingKind(c.has_real, c.has_active)} />
                <span style={{ fontSize: 11, color: "#666", maxWidth: 54, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name.split(" ")[0]}</span>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        {!viewingHidden && (
        <div style={{ display: "flex", borderBottom: "1px solid #F0F0F0", overflowX: "auto" }}>
          {TABS.map(tab => {
            const wantType = TAB_TO_TYPE[tab];
            const unread = (wantType ? conversations.filter(c => c.type === wantType) : conversations).reduce((acc, c) => acc + (c.unread || 0), 0);
            return (
              <button key={tab} onClick={() => setActiveTab(tab)} style={{
                flex: 1, padding: "11px 8px", border: "none", cursor: "pointer", whiteSpace: "nowrap", minWidth: 60,
                backgroundColor: "transparent",
                borderBottom: activeTab === tab ? "2px solid #0a0a0a" : "2px solid transparent",
                color: activeTab === tab ? "#0a0a0a" : "#999",
                fontWeight: activeTab === tab ? 700 : 400, fontSize: 12,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
              }}>
                {t(lang, TAB_I18N_KEY[tab]) || tab}
                {unread > 0 && <span style={{ backgroundColor: "#0a0a0a", color: "white", borderRadius: 10, padding: "1px 5px", fontSize: 9, fontWeight: 700 }}>{unread}</span>}
              </button>
            );
          })}
        </div>
        )}

        {/* Liste conversations — un tap sur l'avatar/nom ouvre la discussion en plein écran sur mobile */}
        <div ref={listScrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
          {viewingHidden && (
            <div onClick={() => setViewingHidden(false)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "13px 16px", borderBottom: "1px solid #F0F0F0", cursor: "pointer", color: "#0a0a0a", fontSize: 13, fontWeight: 700 }}>
              ← {t(lang,"msg_back_to_chats")||"Retour aux discussions"}
            </div>
          )}
          {(viewingHidden ? hiddenConversations : filtered).length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#999" }}>
              <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}>{viewingHidden ? <LockIcon size={44} color="#ccc" /> : <ChatEmptyIcon />}</div>
              <div style={{ fontSize: 14 }}>{viewingHidden ? (t(lang,"msg_no_hidden_chats")||"Aucune discussion masquée") : (t(lang,"notif_empty")||"Aucune conversation")}</div>
            </div>
          ) : (viewingHidden ? hiddenConversations : filtered).map(conv => (
            <ConversationRow key={conv.id} conv={conv} isSelected={selected?.id === conv.id} onClick={() => goToConversation(conv)} myUserId={user?.id} lang={lang} />
          ))}

          {/* Accès aux discussions masquées — geste "tirer et maintenir" (façon archives
              WhatsApp), pas un scroll classique : voir HiddenChatsHandle. Vit dans une petite
              zone dédiée en fin de liste, jamais sur toute la hauteur, pour ne jamais gêner le
              scroll normal des conversations au-dessus. */}
          {!viewingHidden && <HiddenChatsHandle lang={lang} onComplete={openHiddenChats} />}
        </div>
      </div>
      )}

      {/* PANNEAU DROIT — chat. Plein écran sur mobile (comme Messenger) : Layout retire lui-même
          ses propres barres pour la route /messages/:id, donc pas besoin ici de position fixed
          ni de recalcul de hauteur — un simple flex:1 suffit à occuper tout l'espace donné. */}
      {showChat && selected && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", position: "relative",
          // Fond uni — l'image décorative façon WhatsApp (chat-bg.jpg) a été retirée à la demande
          // du fondateur, sa qualité étant en retrait par rapport au fond de la version mobile.
          backgroundColor: "#F5F5F5",
        }}>

          <ChatHeader
            selected={selected}
            typingUser={typingUser}
            lang={lang}
            onBack={() => navigate("/messages")}
            onAvatarClick={() => handleAvatarClick(selected)}
            onVoiceCall={() => startCall({ id: selected.other_user_id!, name: selected.name, avatar: selected.avatar_url }, selected.id, "voice")}
            onVideoCall={() => startCall({ id: selected.other_user_id!, name: selected.name, avatar: selected.avatar_url }, selected.id, "video")}
            onWatchTogether={() => setShowWatchModal(true)}
            onOpenSettings={() => setShowSettingsPanel(true)}
          />

          <div ref={chatScrollRef} onScroll={handleChatScroll} onContextMenu={e => e.preventDefault()} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "20px 24px" }}>
            {/* Bandeau de prudence — voir le plan "Trois emprunts..." : deux signaux ensemble
                (compte récent + zéro ami commun), jamais un seul isolé, pour ne pas déclencher
                sur la simple jeunesse d'OLTODO côté amis en commun. */}
            {safetySignal && safetySignal.mutual_friends_count === 0 && safetySignal.account_age_days < 14 && !safetyBannerDismissed && (
              <div style={{ backgroundColor: "#FFF8E1", border: "1px solid #FFE0A3", borderRadius: 14, padding: 14, marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>⚠️</span>
                  <div style={{ flex: 1, fontSize: 12.5, color: "#7a5c00", lineHeight: 1.5 }}>
                    {t(lang, "safety_banner_text") || "Peu d'informations en commun avec cette personne, sois prudent·e, surtout avant de partager des informations personnelles ou de te rendre à un rendez-vous."}
                  </div>
                  <button onClick={() => setSafetyBannerDismissed(true)} style={{ border: "none", background: "none", cursor: "pointer", color: "#7a5c00", fontSize: 16, padding: 0, flexShrink: 0 }}>✕</button>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                  <button onClick={blockFromSafetyBanner} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid #7a5c00", backgroundColor: "transparent", color: "#7a5c00", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {t(lang, "safety_block_action") || "Bloquer"}
                  </button>
                  <button onClick={() => setShowReportField(v => !v)} style={{ padding: "6px 14px", borderRadius: 20, border: "1px solid #7a5c00", backgroundColor: "transparent", color: "#7a5c00", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                    {t(lang, "safety_report_action") || "Signaler"}
                  </button>
                </div>
                {showReportField && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <input
                      value={reportReason} onChange={e => setReportReason(e.target.value)} onKeyDown={e => e.key === "Enter" && submitSafetyReport()}
                      placeholder={t(lang, "safety_report_placeholder") || "Motif du signalement..."}
                      style={{ flex: 1, padding: "8px 12px", borderRadius: 10, border: "1px solid #FFE0A3", fontSize: 12.5, outline: "none" }}
                    />
                    <button onClick={submitSafetyReport} disabled={!reportReason.trim() || submittingReport} style={{ padding: "8px 14px", borderRadius: 10, border: "none", backgroundColor: "#7a5c00", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      {t(lang, "safety_report_send") || "Envoyer"}
                    </button>
                  </div>
                )}
              </div>
            )}
            {hasMoreOlder && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
                <button onClick={loadOlderMessages} disabled={loadingOlder} style={{ padding: "8px 16px", borderRadius: 16, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#666", fontSize: 12.5, fontWeight: 600, cursor: loadingOlder ? "default" : "pointer", opacity: loadingOlder ? 0.6 : 1 }}>
                  {loadingOlder ? (t(lang,"msg_loading")||"Chargement...") : (t(lang,"msg_load_older")||"Charger les messages précédents")}
                </button>
              </div>
            )}
            {chatMessages.length === 0 && !loadingOlder && (
              // Jamais "chiffrement de bout en bout" ni "seules les personnes de cette discussion
              // peuvent les lire" — le contenu est chiffré au repos avec une clé serveur, pas de
              // bout en bout, un compte master peut le consulter pour modération. Voir la section
              // Sécurité de la politique de confidentialité.
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 30, textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <LockIcon size={13} color="#999" />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#999" }}>{t(lang, "chat_encrypted_banner_title")}</span>
                </div>
                <a href="/privacy-policy" target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 700, color: "#0a0a0a", textDecoration: "none" }}>
                  {t(lang, "chat_encrypted_banner_learn_more")}
                </a>
              </div>
            )}
            {chatMessages.map((msg, index) => {
              const prevDay = index > 0 ? new Date(chatMessages[index - 1].created_at).toDateString() : null;
              const thisDay = new Date(msg.created_at).toDateString();
              const dateSeparator = thisDay !== prevDay ? (
                <div key={`sep-${msg.id}`} style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: "#999", backgroundColor: "#EDEDED", borderRadius: 12, padding: "4px 12px" }}>
                    {dateSeparatorLabel(msg.created_at, lang)}
                  </span>
                </div>
              ) : null;
              const unreadDivider = unreadDividerMsgId === msg.id ? (
                <div key={`unread-${msg.id}`} style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "white", backgroundColor: "#0a0a0a", borderRadius: 12, padding: "4px 14px" }}>
                    {t(lang,"msg_new_messages")||"Nouveaux messages"}
                  </span>
                </div>
              ) : null;

              if (msg.type === "date_confirmed") {
                return (
                  <React.Fragment key={msg.id}>
                    {dateSeparator}{unreadDivider}
                    <DateConfirmedMessage lang={lang} raw={msg.content || ""} />
                  </React.Fragment>
                );
              }
              if (msg.type === "call") {
                const isVideoCall = (() => { try { return JSON.parse(msg.content || "{}").call_type === "video"; } catch { return false; } })();
                return (
                  <React.Fragment key={msg.id}>
                    {dateSeparator}{unreadDivider}
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "white", border: "1px solid #F0F0F0", borderRadius: 20, padding: "6px 14px", fontSize: 12, color: "#666" }}>
                        {isVideoCall ? <VideoCallIcon /> : <CallIcon />}
                        <span>{callLogLabel(msg.content, lang)}</span>
                        <span style={{ color: "#bbb" }}>· {formatBubbleTime(msg.created_at)}</span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }
              // Couvre aussi une invitation envoyée avant l'introduction du type "watch_room"
              // (simple texte "🎬 Regarder ensemble : <lien>") — extractLegacyWatchRoomId repère
              // le lien et la bulle redevient la même pastille moderne, pas de titre disponible
              // pour ce format ancien.
              const legacyWatchRoomId = msg.type !== "watch_room" ? extractLegacyWatchRoomId(msg.content) : null;
              if (msg.type === "watch_room" || legacyWatchRoomId) {
                const watchInfo = msg.type === "watch_room"
                  ? (() => { try { return JSON.parse(msg.content || "{}"); } catch { return {}; } })()
                  : { room_id: legacyWatchRoomId };
                return (
                  <React.Fragment key={msg.id}>
                    {dateSeparator}{unreadDivider}
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 10 }}>
                      <div
                        onClick={() => watchInfo.room_id && navigate(`/watch/${watchInfo.room_id}`)}
                        style={{ display: "flex", alignItems: "center", gap: 8, backgroundColor: "white", border: "1px solid #F0F0F0", borderRadius: 20, padding: "8px 16px", fontSize: 12.5, color: "#0a0a0a", cursor: watchInfo.room_id ? "pointer" : "default", fontWeight: 600, maxWidth: "80%" }}
                      >
                        <WatchTogetherIcon />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {t(lang, "watch_together_cta") || "Regarder ensemble"}{watchInfo.video_title ? ` : ${watchInfo.video_title}` : ""}
                        </span>
                        <span style={{ color: "#bbb", flexShrink: 0 }}>· {formatBubbleTime(msg.created_at)}</span>
                      </div>
                    </div>
                  </React.Fragment>
                );
              }
              const isMe = msg.user_id === user?.id;
              const meta = groupedMeta[index] || { isGroupStart: true, isGroupEnd: true };
              const isRead = isMe && !!otherReadAt && msg.created_at <= otherReadAt;
              return (
                <React.Fragment key={msg.id}>
                  {dateSeparator}{unreadDivider}
                  <MessageBubble
                    lang={lang}
                    msg={msg}
                    isMe={isMe}
                    isGroup={selected.type !== "direct"}
                    showAvatar={meta.isGroupStart}
                    isGroupEnd={meta.isGroupEnd}
                    isRead={isRead}
                    revealed={revealedIds.has(msg.id)}
                    onToggleReveal={() => toggleReveal(msg.id)}
                    menuOpen={messageMenuId === msg.id}
                    onToggleMenu={() => setMessageMenuId(v => v === msg.id ? null : msg.id)}
                    onCloseMenu={() => setMessageMenuId(null)}
                    onEdit={() => startEditMessage(msg)}
                    onDelete={() => deleteMessage(msg)}
                    onRetry={() => retryMessage(msg)}
                  />
                </React.Fragment>
              );
            })}
          </div>

          {/* Bouton "aller en bas" — apparaît dès qu'on n'est plus tout en bas du fil, ou
              qu'un nouveau message arrive pendant qu'on lit plus haut dans l'historique. */}
          {!isAtBottom && (
            <button onClick={scrollToBottom} title={t(lang,"msg_scroll_bottom")} style={{
              position: "absolute", bottom: 92, right: 20, zIndex: 50,
              width: 38, height: 38, borderRadius: "50%", border: "none", cursor: "pointer",
              backgroundColor: "white", boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0a0a0a" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
              {hasNewMessagesBelow && <span style={{ position: "absolute", top: -1, right: -1, width: 10, height: 10, borderRadius: "50%", backgroundColor: "#0a0a0a", border: "2px solid white" }} />}
            </button>
          )}

          <Composer
            lang={lang}
            inputText={inputText}
            onChangeText={setInputText}
            onSend={sendMessage}
            onTyping={handleTyping}
            editingMessageId={editingMessageId}
            onCancelEdit={cancelEditMessage}
            showAttachMenu={showAttachMenu}
            onToggleAttachMenu={() => setShowAttachMenu(v => !v)}
            onShareLocation={shareLocation}
            fileInputRef={fileInputRef}
            onSendFile={sendFile}
            cameraInputRef={cameraInputRef}
            galleryInputRef={galleryInputRef}
            onGallerySelect={handleGallerySelect}
            recording={recording}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            voicePreview={voicePreview}
            onDiscardVoicePreview={discardVoicePreview}
            onSendVoicePreview={sendVoicePreview}
            sendingVoice={sendingVoice}
            uploadingMedia={uploadingMedia}
          />

          {/* Personnaliser la discussion */}
          {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
              position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
              "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
              vrai viewport. */}
          {showSettingsPanel && ReactDOM.createPortal(
            <div onClick={() => setShowSettingsPanel(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 600, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
              <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "82vh", overflowY: "auto", boxSizing: "border-box" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                  <span style={{ fontSize: 17, fontWeight: 700 }}>{t(lang,"msg_customize_title")||"Personnaliser la discussion"}</span>
                  <button onClick={() => setShowSettingsPanel(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
                </div>

                <div style={{ padding: "10px 4px 14px", borderBottom: "1px solid #F5F5F5" }}>
                  <div onClick={() => updateConvSettings({ disappearing: !selected.disappearing })} style={{ display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>{t(lang,"msg_ephemeral_toggle")||"Discussion éphémère"} <FlameIcon /></div>
                      <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{t(lang,"msg_ephemeral_sub")||"Les messages disparaissent après avoir été lus."}</div>
                    </div>
                    <ToggleSwitch on={!!selected.disappearing} />
                  </div>
                  {selected.disappearing && (
                    <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                      {DISAPPEAR_OPTIONS.map(opt => {
                        const active = (selected.disappearing_duration_seconds || 8) === opt.v;
                        return (
                          <button key={opt.v} onClick={() => updateConvSettings({ disappearing_duration_seconds: opt.v })} style={{
                            padding: "6px 14px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer",
                            border: active ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0",
                            backgroundColor: active ? "#0a0a0a" : "white", color: active ? "white" : "#666",
                          }}>{opt.label}</button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div onClick={() => updateConvSettings({ pinned: !selected.pinned })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 4px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <PinIcon size={18} />
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{t(lang,"msg_customize_pin")||"Épingler la discussion"}</div>
                  <ToggleSwitch on={!!selected.pinned} />
                </div>

                <div onClick={() => updateConvSettings({ muted: !selected.muted })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 4px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <MuteIcon size={18} />
                  <div style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{t(lang,"msg_customize_mute")||"Mettre en sourdine"}</div>
                  <ToggleSwitch on={!!selected.muted} />
                </div>

                <div onClick={() => updateConvSettings({ hidden: !selected.hidden })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 4px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <EyeOffIcon />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{selected.hidden ? (t(lang,"msg_customize_unhide")||"Ne plus masquer") : (t(lang,"msg_customize_hide")||"Masquer la discussion")}</div>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{t(lang,"msg_customize_hide_sub")||"Accessible ensuite via le code, en tirant vers le bas de la liste."}</div>
                  </div>
                </div>

                {/* Réglage désormais réel (persisté, actif côté mobile) même si le web lui-même
                    ne peut ni bloquer ni détecter quoi que ce soit — le sous-texte reste donc
                    honnête tel quel, seule la ligne cesse d'être décorative. */}
                <div onClick={() => updateConvSettings({ block_screenshots: !selected.block_screenshots })} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 4px", cursor: "pointer", borderBottom: "1px solid #F5F5F5" }}>
                  <ScreenshotOffIcon />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t(lang,"msg_customize_screenshot_off")||"Bloquer les captures d'écran"}</div>
                    <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{t(lang,"msg_customize_screenshot_sub")||"Impossible à garantir dans un navigateur, seule une application installée sur le téléphone peut le faire."}</div>
                  </div>
                  <ToggleSwitch on={!!selected.block_screenshots} />
                </div>

                <div onClick={deleteConversation} style={{ display: "flex", alignItems: "center", gap: 12, padding: "16px 4px 4px", cursor: "pointer", color: "#c0392b" }}>
                  <TrashIcon />
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{t(lang,"msg_customize_delete")||"Supprimer la discussion"}</div>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}

      {!selected && !isMobile && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#999", gap: 12 }}>
          <ChatEmptyIcon />
          <div style={{ fontSize: 16, fontWeight: 600, color: "#666" }}>{t(lang,"msg_select")||"Sélectionnez une conversation"}</div>
          <div style={{ fontSize: 13 }}>{t(lang,"msg_choose_conversation_sub")||"Choisissez une conversation dans la liste"}</div>
        </div>
      )}

      {/* Code des discussions masquées */}
      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showPinModal && ReactDOM.createPortal(
        <div onClick={() => setShowPinModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 28, width: "100%", maxWidth: 340, textAlign: "center", boxSizing: "border-box" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12, color: "#0a0a0a" }}><LockIcon size={30} /></div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>
              {pinMode === "setup" ? (t(lang,"msg_pin_title_setup")||"Créer un code") : pinMode === "confirm" ? (t(lang,"msg_pin_confirm_title")||"Confirmez le code") : (t(lang,"msg_pin_verify_title")||"Discussions masquées")}
            </div>
            <div style={{ fontSize: 13, color: "#999", marginBottom: 18 }}>
              {pinMode === "setup" ? (t(lang,"msg_pin_setup_body")||"Choisissez un code à 4-6 chiffres pour protéger vos discussions masquées.")
                : pinMode === "confirm" ? (t(lang,"msg_pin_confirm_body")||"Entrez à nouveau le même code pour le confirmer.")
                : (t(lang,"msg_pin_verify_body")||"Entrez votre code pour y accéder.")}
            </div>
            <input type="password" inputMode="numeric" maxLength={6} value={pinValue} onChange={e => { setPinValue(e.target.value.replace(/\D/g, "")); setPinError(""); }} onKeyDown={e => e.key === "Enter" && submitPin()} autoFocus style={{ width: "100%", textAlign: "center", fontSize: 22, letterSpacing: 8, padding: "12px 0", borderRadius: 12, border: "1px solid #E0E0E0", marginBottom: 10, boxSizing: "border-box" }} />
            {pinError && <div style={{ color: "#c0392b", fontSize: 12, marginBottom: 10 }}>{pinError}</div>}
            <button onClick={submitPin} style={{ width: "100%", padding: "12px 0", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>{t(lang,"msg_pin_submit")||"Valider"}</button>
            <button onClick={() => setShowPinModal(false)} style={{ width: "100%", padding: "10px 0", background: "none", border: "none", color: "#999", fontSize: 13, marginTop: 6, cursor: "pointer" }}>{t(lang,"cancel")||"Annuler"}</button>
          </div>
        </div>,
        document.body
      )}

      {showWatchModal && ReactDOM.createPortal(
        <div onClick={() => !creatingWatchRoom && setShowWatchModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 28, width: "100%", maxWidth: 380, boxSizing: "border-box" }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>{t(lang,"watch_together_title")||"Regarder ensemble"}</div>
            <div style={{ fontSize: 13, color: "#999", marginBottom: 16, lineHeight: 1.5 }}>{t(lang,"watch_together_body")||"Colle un lien YouTube, tout le monde dans cette discussion pourra le regarder en même temps que toi."}</div>
            <input
              value={watchUrl} onChange={e => setWatchUrl(e.target.value)} onKeyDown={e => e.key === "Enter" && startWatchTogether()}
              placeholder="https://youtube.com/watch?v=..." autoFocus
              style={{ width: "100%", padding: "12px 14px", borderRadius: 12, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box" }}
            />
            <button onClick={startWatchTogether} disabled={!watchUrl.trim() || creatingWatchRoom} style={{ width: "100%", padding: "12px 0", borderRadius: 12, border: "none", backgroundColor: watchUrl.trim() ? "#0a0a0a" : "#E0E0E0", color: "white", fontWeight: 700, fontSize: 14, cursor: watchUrl.trim() ? "pointer" : "not-allowed" }}>
              {creatingWatchRoom ? "..." : (t(lang,"watch_together_cta")||"Regarder ensemble")}
            </button>
          </div>
        </div>,
        document.body
      )}

      {confirmDeleteMsg && (
        <ConfirmSheet
          title={t(lang,"msg_delete_confirm_title")||"Supprimer ce message ?"}
          confirmLabel={t(lang,"msg_delete_action")||"Supprimer"}
          onConfirm={performDeleteMessage}
          onCancel={() => setConfirmDeleteMsg(null)}
        />
      )}
      {confirmDeleteConv && (
        <ConfirmSheet
          title={t(lang,"msg_delete_conv_title")||"Supprimer cette discussion ?"}
          description={t(lang,"msg_delete_conv_body")||"Elle disparaîtra de votre liste."}
          confirmLabel={t(lang,"msg_delete_action")||"Supprimer"}
          onConfirm={performDeleteConversation}
          onCancel={() => setConfirmDeleteConv(false)}
        />
      )}

      {/* Notice transitoire — remplace les alert() natifs (upload/édition/suppression en échec,
          rate-limit d'envoi...) par un petit bandeau sombre cohérent avec le reste de l'appli. */}
      {notice && (
        <div style={{ position: "fixed", left: "50%", transform: "translateX(-50%)", bottom: isMobile ? 78 : 90, zIndex: 2200, backgroundColor: "#1c1c1e", color: "white", padding: "9px 16px", borderRadius: 20, fontSize: 12.5, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.25)", maxWidth: "88%", textAlign: "center" }}>
          {notice}
        </div>
      )}
    </div>
  );
}
